import { createState } from "../helpers/state-subject";
import { getCurrentPosition } from "../helpers/utils";
import { ensureGlobalState, logInstanceId } from "../helpers/state";
import { NEAREST_STORE_STORAGE_KEY, STORE_STORAGE_KEY, STORE_SELECTION_SOURCES, STORE_MANUALLY_SELECTED_EVENT, } from "../constants/store.constants";
import { emitShopifyEvent } from "./analytics.service";
logInstanceId("store.service");
const GENERIC_HEADERS = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
};
function getStoreLookupBaseUrl() {
    return window.UNTUCKitConfig.storeLookupUrl;
}
export class StoreLookupError extends Error {
    status;
    constructor(status) {
        super('Error getting store.');
        this.status = status;
    }
}
export const selectedStoreState = ensureGlobalState("untuckit.selectedStore", createState(null));
function getValidationTtlMs() {
    const hours = window.UNTUCKitConfig.nearbyToggle.storeValidationTtlHours;
    return hours * 60 * 60 * 1000;
}
export function isValidationExpired(lastValidatedAt) {
    return Date.now() - lastValidatedAt >= getValidationTtlMs();
}
function parseStoredSelection(raw) {
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        if (!parsed?.store?.COLLECTION_HANDLE || !parsed.source || !parsed.lastValidatedAt) {
            return null;
        }
        return parsed;
    }
    catch (e) {
        console.warn("Unable to parse stored store selection", e);
        return null;
    }
}
function readManualSelection() {
    try {
        const parsed = parseStoredSelection(localStorage.getItem(STORE_STORAGE_KEY));
        if (!parsed) {
            return null;
        }
        if (parsed.source !== STORE_SELECTION_SOURCES.MANUAL) {
            clearManualSelection();
            return null;
        }
        return parsed;
    }
    catch (e) {
        console.warn("Unable to read manual store selection", e);
        return null;
    }
}
function writeManualSelection(selection) {
    try {
        localStorage.setItem(STORE_STORAGE_KEY, JSON.stringify({
            ...selection,
            source: STORE_SELECTION_SOURCES.MANUAL,
        }));
    }
    catch (e) {
        console.warn("Unable to persist manual store selection", e);
    }
}
function clearManualSelection() {
    try {
        localStorage.removeItem(STORE_STORAGE_KEY);
    }
    catch (e) {
        console.warn("Unable to clear manual store selection", e);
    }
}
function readNearestStoreSelection() {
    try {
        return parseStoredSelection(sessionStorage.getItem(NEAREST_STORE_STORAGE_KEY));
    }
    catch (e) {
        console.warn("Unable to read nearest store selection", e);
        return null;
    }
}
function writeNearestStoreSelection(store) {
    try {
        sessionStorage.setItem(NEAREST_STORE_STORAGE_KEY, JSON.stringify({
            store,
            source: STORE_SELECTION_SOURCES.AUTO,
            lastValidatedAt: Date.now(),
        }));
    }
    catch (e) {
        console.warn("Unable to persist nearest store selection", e);
    }
}
function clearNearestStoreSelection() {
    try {
        sessionStorage.removeItem(NEAREST_STORE_STORAGE_KEY);
    }
    catch (e) {
        console.warn("Unable to clear nearest store selection", e);
    }
}
function applyManualStoreSelection(store) {
    writeManualSelection({
        store,
        source: STORE_SELECTION_SOURCES.MANUAL,
        lastValidatedAt: Date.now(),
    });
    selectedStoreState.set(store);
}
function applyNearestStoreSelection(store) {
    writeNearestStoreSelection(store);
    selectedStoreState.set(store);
}
async function resolveManualSelection() {
    const stored = readManualSelection();
    if (!stored) {
        return null;
    }
    if (!isValidationExpired(stored.lastValidatedAt)) {
        return stored.store;
    }
    try {
        const store = await getStoreById(stored.store.COLLECTION_HANDLE);
        writeManualSelection({
            store,
            source: STORE_SELECTION_SOURCES.MANUAL,
            lastValidatedAt: Date.now(),
        });
        return store;
    }
    catch (e) {
        if (e instanceof StoreLookupError && e.status === 404) {
            clearManualSelection();
            return null;
        }
        console.warn("Manual store revalidation failed; using cached selection", e);
        return stored.store;
    }
}
export async function initSelectedStore() {
    const manualStore = await resolveManualSelection();
    if (manualStore) {
        selectedStoreState.set(manualStore);
        return;
    }
    const nearestStored = readNearestStoreSelection();
    if (nearestStored) {
        selectedStoreState.set(nearestStored.store);
        return;
    }
    await detectNearestStore();
}
export async function selectStoreManually(storeHandle) {
    const store = await getStoreById(storeHandle);
    applyManualStoreSelection(store);
    emitShopifyEvent(STORE_MANUALLY_SELECTED_EVENT, {
        collection_handle: store.COLLECTION_HANDLE,
        store_name: store.SHOPIFY_DISPLAY_NAME,
    });
}
async function detectNearestStore() {
    let position = null;
    if (window.UNTUCKitConfig.nearbyToggle.browserBasedGeolocationToggle) {
        try {
            position = await getCurrentPosition();
        }
        catch (e) {
            console.warn("error getting browser-based geolocation position:", e.message || e);
        }
    }
    try {
        const nearestStores = await fetchNearbyStores(position?.coords.latitude, position?.coords.longitude);
        const nearestStore = nearestStores?.[0];
        if (!nearestStore) {
            return;
        }
        const maxDistance = parseInt(window.UNTUCKitConfig.nearbyToggle.nearby_lookup_max_distance, 10);
        if (maxDistance > nearestStore.distance) {
            applyNearestStoreSelection(nearestStore);
        }
        else {
            console.log(`skipping; closest store is ${nearestStore.distance}km away`);
        }
    }
    catch (e) {
        console.error("error fetching nearby stores:", e.message || e);
    }
}
/**
 * Fetch nearby stores from the nearby lookup URL
 * If browser-based geolocation is available, use the lat/long to fetch nearby stores.
 * Otherwise CloudFront may be used as a backup for the lat/long.
 */
async function fetchNearbyStores(latitude, longitude) {
    const queryParams = latitude && longitude ? `?latitude=${latitude}&longitude=${longitude}` : '';
    const res = await fetch(`${window.UNTUCKitConfig.nearbyToggle.nearby_lookup_url}${queryParams}`);
    if (!res.ok) {
        throw new Error("Error when attempting to fetch nearby stores");
    }
    const json = await res.json();
    return json?.stores;
}
export async function getStoreById(storeId) {
    const response = await fetch(`${getStoreLookupBaseUrl()}/${storeId}`, { method: 'GET', headers: GENERIC_HEADERS });
    if (!response.ok) {
        throw new StoreLookupError(response.status);
    }
    return await response.json();
}
export function extractCollectionHandleFromUrl(url) {
    try {
        const pathname = new URL(url, window.location.origin).pathname;
        const match = pathname.match(/\/collections\/([^/?#]+)/i);
        return match ? match[1].toLowerCase() : null;
    }
    catch {
        return null;
    }
}
export async function getInventoryByStoreId(storeId) {
    const response = await fetch(`/collections/${storeId}?view=newstore`);
    if (!response.ok) {
        throw new Error(`Error getting inventory for store: ${storeId}`);
    }
    return await response.json();
}
export function sharedStoreDataMapping(store) {
    return {
        address: store.ADDRESS2.length > 0 ? store.ADDRESS + " " + store.ADDRESS2 : store.ADDRESS,
        city: store.CITY + ", " + store.STATE + " " + store.ZIP,
        storeHref: '/collections/' + store.COLLECTION_HANDLE
    };
}
export const _forTesting = {
    readManualSelection,
    writeManualSelection,
    clearManualSelection,
    readNearestStoreSelection,
    writeNearestStoreSelection,
    clearNearestStoreSelection,
    applyManualStoreSelection,
    applyNearestStoreSelection,
    getValidationTtlMs,
    // Backward-compatible aliases used by existing tests
    readStoredSelection: readManualSelection,
    writeStoredSelection: writeManualSelection,
    clearStoredSelection: clearManualSelection,
};
